import { SecretValue } from "client/Api/Apps/common";
import {
	FormItem,
	SecretValueInput,
	TextInput,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { IntegrationFlowAfasAuthenticationSettings } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";

export type AfasSettingsProps = {
	attachable: IValueInputAttachable<IntegrationFlowAfasAuthenticationSettings>;
	isNew: boolean;
};

export const AfasSettings = (props: AfasSettingsProps): ReactElement => {
	const {
		attachable,
		isNew
	} = props;

	const clearTheApiKey = useCallback(
		(): void => {
			const apiKeyAttachable: IValueInputAttachable<SecretValue> = attachable
				.for("apiKey");

			const { containsNewValue } = apiKeyAttachable
				.reducer
				.value;

			if (containsNewValue) {
				return;
			}

			apiKeyAttachable
				.reducer
				.setValue({
					containsNewValue: true,
					value: ""
				});

			apiKeyAttachable
				.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	return (
		<>
			<FormItem
				label={localize("_ApiUrl")}
				isRequired
			>
				<TextInput
					{...attachable
						.for("apiUrl")
						.attachToTextInput()
						.withOnChange(clearTheApiKey)
					}
					placeholder={localize("_EnterApiUrl")}
				/>

				<ValidationError {...attachable.for("apiUrl").attachToValidationError()}/>
			</FormItem>

			<FormItem
				label={localize("_EnvironmentKey")}
				isRequired
			>
				<TextInput
					{...attachable
						.for("environmentKey")
						.attachToTextInput()
						.withOnChange(clearTheApiKey)
					}
					placeholder={localize("_EnterEnvironmentKey")}
				/>

				<ValidationError {...attachable.for("environmentKey").attachToValidationError()}/>
			</FormItem>

			<FormItem
				label={localize("_ApiKey")}
				isRequired
			>
				<SecretValueInput
					{...attachable.for("apiKey").attachToTextInput()}
					containsInitialValue={!isNew}
					placeholder={localize("_EnterApiKey")}
				/>

				<ValidationError
					{...attachable
						.for("apiKey")
						.for("value")
						.attachToValidationError()
					}
				/>
			</FormItem>
		</>
	);
};
AfasSettings.displayName = "AfasSettings";

export const defaultAfasSettings: IntegrationFlowAfasAuthenticationSettings = {
	apiUrl: "",
	environmentKey: "",
	apiKey: {
		containsNewValue: true,
		value: ""
	},
	oneTimePassword: { isSet: false }
};

export const afasValidationConfiguration: IValidationConfiguration<IntegrationFlowAfasAuthenticationSettings> = createValidationConfiguration<IntegrationFlowAfasAuthenticationSettings>();

afasValidationConfiguration
	.forScalar("apiUrl")
	.add(
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		}),
		doesNotExceedLength(2_048)
	);

afasValidationConfiguration
	.forScalar("environmentKey")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

afasValidationConfiguration
	.forObject("apiKey")
	.when(x => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

afasValidationConfiguration
	.forObject("oneTimePassword")
	.whenType(x => x.isSet)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);